import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import {
  CalendarConnectionStatusDtoStatus,
  NylasGrantDtoProvider,
  useIsCalendarConnected,
  useListGoals
} from '@/api/generated';
import Integrations from '@/components/Integrations/Integrations';
import PopupError from '@/components/Integrations/components/PopupError';
import RoutesMap from '@/enums/RoutesMap';
import useIntegrations from '@/hooks/Integrations/useIntegrations';
import useGeneratePlan from '@/pages/GoalsPage/hooks/useGeneratePlan';
import useGoalsLogic from '@/pages/GoalsPage/hooks/useGoalsLogic';
import { getYearAndQuarter } from '@/utils/dateUtil';
import { useIsSmallScreen } from '@/utils/mediaUtil';
import { actionToast } from '@/utils/toast';

import { Container, HalfContainer, IntegrationsContainer, MarginStack } from './OnboardingStyles';
import Connect from './components/Connect';
import ContinueGenerateButton from './components/ContinueGenrateButton';
import Goals from './components/Goals';
import Oops from './components/OOps';
import ProfessionalGoals from './components/ProfessionalGoals';
import YourPlan from './components/YourPlan';

interface OnboardingProps {
  status: CalendarConnectionStatusDtoStatus;
}

const Onboarding = ({ status }: OnboardingProps) => {
  const [isGoals, setIsGoals] = useState(false);
  const isSmallScreen = useIsSmallScreen();
  const { userGrant } = useIntegrations();
  const hasGoogle = userGrant ? userGrant.provider === NylasGrantDtoProvider.google : false;
  const hasMicrosoft = userGrant ? userGrant.provider === NylasGrantDtoProvider.microsoft : false;
  const hasCalendar = hasGoogle || hasMicrosoft;
  const navigate = useNavigate();
  const { captureEvent } = useAnalytics();
  const { periods, selectedPeriod, setSelectedPeriod, sortedGoals, dueDate, monthToStart } =
    useGoalsLogic();
  const { generateStrategicPlan, isGenerating, generatedStrategicPlan, isGenerateError } =
    useGeneratePlan();

  const { refetch: refetchCalendarConnectionStatus } = useIsCalendarConnected({
    query: { enabled: false }
  });

  useEffect(() => {
    if (isGenerateError) {
      actionToast({
        caption: 'There has been an error generating the strategic plan.',
        action: { label: 'Regenerate', onClick: generatePlan },
        vertical: 'bottom'
      });
    }
  }, [isGenerateError]);

  const { refetch: refetchGoals } = useListGoals(undefined, {
    query: {
      enabled: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      gcTime: Infinity
    }
  });

  const generatePlan = async () => {
    captureEvent(EVENTS.PLAN_GENERATED);
    const res = await refetchGoals();
    const goals = res.data?.items || [];

    const goalsTitles = goals
      .filter(goal => {
        const quarterYear = getYearAndQuarter(new Date(goal.dueDate || ''), monthToStart);
        return (
          quarterYear.year === selectedPeriod.year && quarterYear.quarter === selectedPeriod.quarter
        );
      })
      .map(goal => goal.title) as string[];
    generateStrategicPlan([goalsTitles.join(', ')]);
  };

  const handleContinue = () => {
    if (hasCalendar && !isGoals) {
      setIsGoals(true);
    } else {
      refetchCalendarConnectionStatus();
      navigate(RoutesMap.Home);
    }
  };

  return (
    <Container>
      <PopupError />
      <MarginStack sx={{ ...(isSmallScreen && { margin: '50px 0', height: 'unset' }) }}>
        <HalfContainer
          sx={{
            maxWidth: '470px',
            alignItems: 'flex-start',
            ...(isSmallScreen && { width: '100%', height: 'unset', marginBottom: '100px' })
          }}
        >
          {status === CalendarConnectionStatusDtoStatus.NOT_CONNECTED ? (
            <>
              {!isGoals && !generatedStrategicPlan && <Connect />}
              {isGoals && !generatedStrategicPlan && <ProfessionalGoals />}
              {generatedStrategicPlan && <YourPlan />}
              <ContinueGenerateButton
                hasCalendar={hasCalendar}
                handleContinue={handleContinue}
                generatePlan={generatePlan}
                goals={sortedGoals}
                isGenerating={isGenerating}
                generatedStrategicPlan={generatedStrategicPlan}
                isGenerateError={isGenerateError}
              />
            </>
          ) : (
            <Oops />
          )}
        </HalfContainer>

        <IntegrationsContainer sx={{ ...(isSmallScreen && { width: '100%', height: 'unset' }) }}>
          {isGoals ? (
            <Goals
              periods={periods}
              selectedPeriod={selectedPeriod}
              setSelectedPeriod={setSelectedPeriod}
              sortedGoals={sortedGoals}
              dueDate={dueDate || ''}
              monthToStart={monthToStart}
              generatePlan={generatePlan}
              isGenerating={isGenerating}
              generatedStrategicPlan={generatedStrategicPlan}
              isGenerateError={isGenerateError}
            />
          ) : (
            <Integrations
              sx={{ ...(isSmallScreen && { height: 'fit-content', overflowY: 'hidden' }) }}
            />
          )}
        </IntegrationsContainer>
      </MarginStack>
    </Container>
  );
};

export default Onboarding;
